import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { fetchTenantAttendanceMetrics } from '../services/tenantMetrics';
import { transformKeys } from '../services/transformers';
import { logTenantMetricsDebug } from '../utils/tenantMetricsDebug';
import type {
  TenantAttendanceMetricsHumanAttendance,
  TenantAttendanceMetricsResponse,
  TenantAttendanceMetricsTelephony,
} from '../types';

interface TenantAttendanceMetricsContextValue {
  isConnected: boolean;
  isSubscribed: boolean;
  isRefreshing: boolean;
  telephony: TenantAttendanceMetricsTelephony | null;
  humanAttendance: TenantAttendanceMetricsHumanAttendance | null;
  lastPayload: TenantAttendanceMetricsResponse | null;
  canViewAttendanceMetrics: boolean;
  refresh: (reason?: string) => Promise<void>;
}

const TenantAttendanceMetricsContext = createContext<TenantAttendanceMetricsContextValue | undefined>(undefined);

/**
 * Provide one shared tenant attendance metrics store for shell consumers.
 */
export function TenantAttendanceMetricsProvider({ children }: { children: ReactNode }) {
  const { currentTenant, user, capabilities } = useAuth();
  const { state, addObserver, removeObserver } = useWebSocket();
  const tenantId = currentTenant?.id || user?.tenantId || null;
  const canViewAttendanceMetrics = Boolean(capabilities?.canViewAttendanceMetrics);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [telephony, setTelephony] = useState<TenantAttendanceMetricsTelephony | null>(null);
  const [humanAttendance, setHumanAttendance] = useState<TenantAttendanceMetricsHumanAttendance | null>(null);
  const [lastPayload, setLastPayload] = useState<TenantAttendanceMetricsResponse | null>(null);
  const logDebug = useCallback((message: string, data?: unknown) => {
    logTenantMetricsDebug('TenantAttendanceMetricsProvider', message, data);
  }, []);

  useEffect(() => {
    logDebug('tenant changed, resetting cached attendance metrics state', { tenantId });
    setTelephony(null);
    setHumanAttendance(null);
    setLastPayload(null);
  }, [logDebug, tenantId]);

  const refresh = useCallback(async (reason = 'manual_recovery') => {
    if (!tenantId || !canViewAttendanceMetrics) {
      logDebug('refresh skipped because tenant or permission is missing', {
        reason,
        tenantId,
        canViewAttendanceMetrics,
      });
      return;
    }

    logDebug('refresh started', {
      reason,
      tenantId,
      isConnected: state.isConnected,
      hasLastPayload: Boolean(lastPayload),
    });
    setIsRefreshing(true);
    try {
      const response = await fetchTenantAttendanceMetrics();
      setTelephony(response.telephony ?? null);
      setHumanAttendance(response.humanAttendance ?? null);
      setLastPayload(response);
      logDebug('refresh completed', {
        reason,
        tenantId,
        hasTelephony: Boolean(response.telephony),
        hasHumanAttendance: Boolean(response.humanAttendance),
      });
    } catch (error) {
      logDebug('refresh failed', {
        reason,
        tenantId,
        error,
      });
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [canViewAttendanceMetrics, lastPayload, logDebug, state.isConnected, tenantId]);

  const handleEvent = useCallback((data: TenantAttendanceMetricsResponse, action: string) => {
    if (action !== 'create' && action !== 'update' && action !== 'delete') {
      logDebug('event ignored because action is not handled', { tenantId, action, data });
      return;
    }

    const normalized = transformKeys<TenantAttendanceMetricsResponse>(data);
    logDebug('event received', {
      tenantId,
      action,
      hasTelephony: Boolean(normalized.telephony),
      hasHumanAttendance: Boolean(normalized.humanAttendance),
      payload: normalized,
    });
    setTelephony(normalized.telephony ?? null);
    setHumanAttendance(normalized.humanAttendance ?? null);
    setLastPayload(normalized);
  }, [logDebug, tenantId]);

  useEffect(() => {
    if (!tenantId || !canViewAttendanceMetrics || !state.isConnected) {
      logDebug('subscription skipped because preconditions are not satisfied', {
        tenantId,
        canViewAttendanceMetrics,
        isConnected: state.isConnected,
      });
      setIsSubscribed(false);
      return;
    }

    logDebug('creating attendance metrics observer subscription', {
      tenantId,
      canViewAttendanceMetrics,
      isConnected: state.isConnected,
    });
    const observerId = addObserver(
      'tenant_attendance_metrics',
      { data: { target: tenantId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
        includeUpdateEvents: true,
        includeDeleteEvents: true,
      },
    );
    setIsSubscribed(true);
    logDebug('attendance metrics observer subscribed', { tenantId, observerId });

    return () => {
      logDebug('cleaning up attendance metrics observer subscription', { tenantId, observerId });
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [addObserver, canViewAttendanceMetrics, handleEvent, logDebug, removeObserver, state.isConnected, tenantId]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const refreshIfRecoveryIsNeeded = () => {
      if (!tenantId || !canViewAttendanceMetrics) {
        logDebug('recovery skipped because tenant or permission is missing', {
          tenantId,
          canViewAttendanceMetrics,
        });
        return;
      }
      if (state.isConnected && lastPayload) {
        logDebug('recovery skipped because provider believes connection is healthy', {
          tenantId,
          isConnected: state.isConnected,
          hasLastPayload: Boolean(lastPayload),
        });
        return;
      }
      logDebug('recovery refresh requested', {
        tenantId,
        isConnected: state.isConnected,
        hasLastPayload: Boolean(lastPayload),
      });
      void refresh('attendance_metrics_recovery');
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        logDebug('visibilitychange ignored because document is not visible', {
          tenantId,
          documentVisibilityState: document.visibilityState,
        });
        return;
      }
      logDebug('visibilitychange detected visible document for recovery check', { tenantId });
      refreshIfRecoveryIsNeeded();
    };

    window.addEventListener('focus', refreshIfRecoveryIsNeeded);
    window.addEventListener('pageshow', refreshIfRecoveryIsNeeded);
    window.addEventListener('online', refreshIfRecoveryIsNeeded);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshIfRecoveryIsNeeded);
      window.removeEventListener('pageshow', refreshIfRecoveryIsNeeded);
      window.removeEventListener('online', refreshIfRecoveryIsNeeded);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [canViewAttendanceMetrics, lastPayload, logDebug, refresh, state.isConnected, tenantId]);

  const value = useMemo<TenantAttendanceMetricsContextValue>(
    () => ({
      isConnected: state.isConnected,
      isSubscribed,
      isRefreshing,
      telephony,
      humanAttendance,
      lastPayload,
      canViewAttendanceMetrics,
      refresh,
    }),
    [
      canViewAttendanceMetrics,
      humanAttendance,
      isRefreshing,
      isSubscribed,
      lastPayload,
      refresh,
      state.isConnected,
      telephony,
    ],
  );

  return (
    <TenantAttendanceMetricsContext.Provider value={value}>
      {children}
    </TenantAttendanceMetricsContext.Provider>
  );
}

export function useTenantAttendanceMetricsContext(): TenantAttendanceMetricsContextValue {
  const context = useContext(TenantAttendanceMetricsContext);
  if (!context) {
    throw new Error('useTenantAttendanceMetricsContext must be used within TenantAttendanceMetricsProvider');
  }
  return context;
}
